import { FileEntry } from "capitalrx-components-2.0/lib/forms";
import { AxiosError } from "axios";


export const DESIGNATION_TYPE_VALUES = {
  EIN: "ein",
  SSN: "ssn",
};
type DesignationTypeKey = keyof typeof DESIGNATION_TYPE_VALUES;
export type DesignationTypeValue = typeof DESIGNATION_TYPE_VALUES[DesignationTypeKey];


export const PROVIDER_TYPE_VALUES = {
  MEDICAL: "medical",
  PHARMACY: "pharmacy",
};
type ProviderTypeKey = keyof typeof PROVIDER_TYPE_VALUES;
export type ProviderTypeValue = typeof PROVIDER_TYPE_VALUES[ProviderTypeKey];


export const REMITTANCE_TYPE_VALUES = {
  ELECTRONIC: "electronic",
  PAPER: "paper",
};
type RemittanceTypeKey = keyof typeof REMITTANCE_TYPE_VALUES;
export type RemittanceTypeValue = typeof REMITTANCE_TYPE_VALUES[RemittanceTypeKey];


export const PAYMENT_TYPE_VALUES = {
  ACH: "ach",
  CHECK: "check",
  VCC: "vcc",
};
type PaymentTypeKey = keyof typeof PAYMENT_TYPE_VALUES;
export type PaymentTypeValue = typeof PAYMENT_TYPE_VALUES[PaymentTypeKey];

export const PAYMENT_TYPE_INFORMATIONAL_MESSAGES = {
  ACH: "Remittance will be distributed electronically. 835s will be available for download in the Provider Portal.",
  CHECK: "Checks and paper EOPs will be sent to the Provider’s mailing address.",
  VCC: "",
};
export const ACCOUNT_TYPE_VALUES = {
  CHECKING: "checking",
  SAVINGS: "savings",
};
type AccountTypeKey = keyof typeof ACCOUNT_TYPE_VALUES;
export type AccountTypeValue = typeof ACCOUNT_TYPE_VALUES[AccountTypeKey];


export const DOCUMENT_TYPE_VALUES = {
  AUTHORIZATION_LETTER_FOR_EFT: "authorization_letter_for_eft",
  BANK_LETTERHEAD: "bank_letterhead",
  VOIDED_CHECK: "voided_check",
  W9: "w9",
};
type DocumentTypeKey = keyof typeof DOCUMENT_TYPE_VALUES;
export type DocumentTypeValue = typeof DOCUMENT_TYPE_VALUES[DocumentTypeKey];


export interface ProviderCreateRequest {
  readonly provider_type: ProviderTypeValue;
  readonly provider_tax_id_number: string;
  readonly tax_id_number_designation: DesignationTypeValue;
  readonly provider_billing_npi: string;
  readonly legal_business_name: string;
  readonly doing_business_as_name: string;
  readonly pharmacy_nabp_id: string;
  readonly pharmacy_chain_code: string;
}

export interface ProviderCreateResponse {
  readonly provider_management_id: number;
  readonly provider_enrollment_status: string;
  readonly provider_billing_npi: string;
}

export interface DocumentSchema {
  id: number;
  bucket: string;
  key: string;
  file_name: string;
  document_type: string;
  content_type: string;
  file_size: number;
  notes: string;
  provider_billing_npi?: string;
  provider_tax_id_number?: string;
  identity?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ProviderSchema {
  id: number;
  provider_id: number;
  type: ProviderTypeValue;
  billing_npi: string;
  tax_id_number: string;
  tax_id_number_designation: DesignationTypeValue;
  enrollment_status: string;

  legal_business_name: string;
  doing_business_as_name: string;
  pharmacy_nabp_id: string;
  pharmacy_chain_code: string;

  physical_address_line_1: string;
  physical_address_line_2: string;
  physical_address_city: string;
  physical_address_state: string;
  physical_address_zip_code: string;

  mailing_address_line_1: string;
  mailing_address_line_2: string;
  mailing_address_city: string;
  mailing_address_state: string;
  mailing_address_zip_code: string;

  authorized_official_first_name: string;
  authorized_official_last_name: string;
  authorized_official_title: string;
  authorized_official_phone: string;
  authorized_official_email: string;
  authorized_official_fax: string;

  primary_contact_first_name: string;
  primary_contact_last_name: string;
  primary_contact_title: string;
  primary_contact_phone: string;
  primary_contact_email: string;

  secondary_contact_first_name: string;
  secondary_contact_last_name: string;
  secondary_contact_title: string;
  secondary_contact_phone: string;
  secondary_contact_email: string;

  provider_name: string;
  provider_email: string;
  provider_phone: string;
  provider_fax: string;
  provider_grouping_id: string;

  start_date: string;
  end_date: string;
  effective_date: string;
  termination_date: string;
  deleted_at: string;
  reason_for_deactivation: string;
  created_at: string;
  updated_at: string;

  legal_business_name_options: string[];
  medicare_id_number: string;
  medicaid_id_number: string;
  submitted_date: string;
}

export interface PaymentPreferenceCreateRequest {
  provider_id?: number;
  provider_network_id?: number;
  network_enrollment_status?: string;
};

export interface PaymentPreferenceGetSchema {
  payment_info_id?: number;
  provider_id: number;
  provider_network_id: number;
  network_enrollment_status: string;
}

export interface PaymentPreferenceSchema {
  id: number;
  provider_id?: number;

  created_at?: string;
  updated_at?: string;
  deleted_at?: string;

  network_enrollment_status: string;

  start_date?: string;
  end_date?: string;
  effective_date?: string;
  termination_date?: string;

  provider_network_id?: number;

  remittance_address_line_1?: string;
  remittance_address_line_2?: string;
  remittance_address_city?: string;
  remittance_address_state?: string;
  remittance_address_zip_code?: string;

  financial_institution_name?: string;
  financial_institution_address_line_1?: string;
  financial_institution_address_line_2?: string;
  financial_institution_address_city?: string;
  financial_institution_address_state?: string;
  financial_institution_address_zip_code?: string;
  financial_institution_contact_first_name?: string;
  financial_institution_contact_last_name?: string;
  financial_institution_phone?: string;
  financial_institution_fax?: string;

  payment_type?: string;
  preferred_payment_method?: string;
  ach_account_type?: string;
  ach_routing_number?: string;
  ach_bank_account_number?: string;
  check_number?: string;
  account_owner_first_name?: string;
  account_owner_last_name?: string;
  virtual_credit_card?: string;
  reason_for_deactivation?: string;

  transaction_validation_claim_1_number?: string;
  transaction_validation_claim_1_paid_amount?: string;
  transaction_validation_claim_2_number?: string;
  transaction_validation_claim_2_paid_amount?: string;

  remittance_type?: string;
  remittance_recipient_email_addresses?: string;
  remittance_recipient_sftp_delivery_targets?: string;
}

export interface PaymentPreference {
  provider_payment_info: PaymentPreferenceSchema;
}

export interface PaymentPreferenceListSearchOrderBy {
  provider_id?: number;
  provider_network_id?: number;
  network_enrollment_status?: string;
  payment_type?: string;
  remittance_type?: string;
  updated_at?: string;
  created_at?: string;
};

export interface PaymentPreferenceListSearchRequest {
  page_number?: number;
  results_per_page?: number;
  expected_fields?: Array<any>;
  provider_id?: number;
  provider_network_id?: number;
  network_enrollment_status?: string;
  in_network?: boolean;
  payment_type?: string;
  remittance_type?: string;
  search_text?: string;
  created_at?: string;
  updated_at?: string;
  order_by?: Array<PaymentPreferenceListSearchOrderBy>;
};

export interface PaymentPreferenceListSearchResponseResult {
  provider_payment_info?: PaymentPreferenceSchema;
};

export interface PaymentPreferenceListSearchResponse {
  results?: Array<PaymentPreferenceListSearchResponseResult>;
  result_count?: string;
};

export interface ProviderGetResponse {
  readonly provider: ProviderSchema;
  readonly documents: DocumentSchema[];
  readonly payment_preferences: PaymentPreference[];
}

export interface ProviderUpdateResponse {
  message: string;
}


export interface PresignedPost {
  readonly url: string;
  readonly fields: Record<string, string>;
}

export interface SuccessfulFileUpload {
  readonly id: number;
  readonly bucket: string;
  readonly key: string;
  readonly file_name: string;
  readonly document_type: string;
  readonly content_type: string;
  readonly file_size?: number;
  readonly notes?: string;
  readonly post: PresignedPost;
}

export interface FileUploadError {
  readonly file_index: number;
  readonly message: string;
  readonly errors?: Record<string, AxiosError>;
  readonly detail?: string;
}

export interface ProviderDocumentFileUploadResponse {
  readonly message: string;
  readonly successful_files?: SuccessfulFileUpload[];
  readonly errors?: FileUploadError[];
}

export interface ProviderDocumentFileUpload {
  readonly file_name?: string;
  readonly document_type?: DocumentTypeValue;
  readonly file_size?: number;
  readonly content_type?: string;
  readonly notes?: string;
  readonly file?: FileEntry;
}
export interface ProviderDocumentFileUploadRequest extends ProviderDocumentFileUpload {
  readonly network_id?: number;
  readonly files?: ProviderDocumentFileUpload[];
}

export interface ProviderNetworkConfig {
  id?: number;
  provider_id?: number;
  provider_network_id?: number;
  payment_info_id?: number;
}

export interface ProviderNetworkConfigListSearchOrderBy {
  network_id?: number;
  provider_network_id?: number;
  payment_info_id?: number;
};

export interface ProviderNetworkConfigListSearchRequest {
  page_number?: number;
  results_per_page?: number;
  expected_fields?: Array<any>;
  provider_id?: number;
  provider_network_id?: number;
  payment_info_id?: number;
  order_by?: Array<ProviderNetworkConfigListSearchOrderBy>;
};

export interface ProviderNetworkConfigListSearchResponseResult {
  provider_network_configuration?: ProviderNetworkConfig;
};

export interface ProviderNetworkConfigListSearchResponse {
  results?: Array<ProviderNetworkConfigListSearchResponseResult>;
  result_count?: string;
};

export type ProviderNetworkListSearchOrderBy = {
  name?: string;
  network_id?: number;
};

export type ProviderNetworkListSearchRequest = {
  page_number?: number;
  results_per_page?: number;
  expected_fields?: Array<any>;
  name?: string;
  network_id?: number;
  order_by?: Array<ProviderNetworkListSearchOrderBy>;
};

export interface NetworkDetails {
  id: number;
  name: string;
  in_network: boolean;
}

export interface ProviderNetwork {
  provider_network: NetworkDetails;
}

export interface ProviderNetworkListSearchResponse {
  results?: Array<ProviderNetwork>;
  result_count?: string;
};
